import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderSummary } from '@tucupet/shared';
import { PageHeader, Card, Btn } from '../components/ui';
import { api } from '../lib/api';

export default function OrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [rescheduleNote, setRescheduleNote] = useState('');
  const { data: order, isLoading } = useQuery<OrderSummary>({
    queryKey: ['admin', 'order', id],
    queryFn: () => api(`/api/admin/orders/${id}`),
    enabled: !!id,
  });

  const surtido = useMutation({
    mutationFn: () => api(`/api/admin/orders/${id}/surtido`, { body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'order', id] }),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/admin/orders/${id}/cancel`, { body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'order', id] }),
  });
  const approveReschedule = useMutation({
    mutationFn: () => api(`/api/admin/orders/${id}/reschedule/approve`, { body: { note: rescheduleNote || undefined } }),
    onSuccess: () => { setRescheduleNote(''); qc.invalidateQueries({ queryKey: ['admin', 'order', id] }); },
  });
  const rejectReschedule = useMutation({
    mutationFn: () => api(`/api/admin/orders/${id}/reschedule/reject`, { body: { note: rescheduleNote } }),
    onSuccess: () => { setRescheduleNote(''); qc.invalidateQueries({ queryKey: ['admin', 'order', id] }); },
  });

  if (isLoading || !order) return <div>Cargando…</div>;

  return (
    <div>
      <Link to="/orders" style={{ color: 'var(--text-2)', fontSize: 13, textDecoration: 'none' }}>← Órdenes</Link>
      <PageHeader
        kicker="ORDEN"
        title={order.code}
        sub={`${order.stationName} · ${order.fuelVariantName} · ${order.liters} L`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            {order.status === 'confirmed' && (
              <Btn onClick={() => surtido.mutate()} disabled={surtido.isPending}>
                {surtido.isPending ? '…' : 'Marcar surtido'}
              </Btn>
            )}
            {!['surtido', 'cancelled', 'expired'].includes(order.status) && (
              <Btn variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                Cancelar orden
              </Btn>
            )}
          </div>
        }
      />

      {order.rescheduleRequest && order.rescheduleRequest.status === 'pending' && (
        <Card style={{ marginTop: 16, borderColor: 'oklch(0.78 0.16 70)' }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'oklch(0.78 0.16 70)' }}>
            REPLANIFICACIÓN EN REVISIÓN · INTENTO {(order.noShowAttempts ?? 0) + 1} DE 2
          </div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 18px', fontSize: 13 }}>
            <div style={{ color: 'var(--text-3)' }}>Slot original</div>
            <div className="mono">{order.slotDate} {order.slotTime} · {order.stationName} · bomba {order.slotPump}</div>
            <div style={{ color: 'var(--text-3)' }}>Solicitado</div>
            <div className="mono">{order.rescheduleRequest.newSlotDate} {order.rescheduleRequest.newSlotTime} · {order.rescheduleRequest.newStationName} · bomba {order.rescheduleRequest.newSlotPump}</div>
            <div style={{ color: 'var(--text-3)' }}>Dirección nueva</div>
            <div>{order.rescheduleRequest.newStationAddr}</div>
            <div style={{ color: 'var(--text-3)' }}>Pedida el</div>
            <div className="mono">{new Date(order.rescheduleRequest.requestedAt).toLocaleString('es-MX')}</div>
          </div>
          <div style={{ marginTop: 14 }}>
            <label className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em' }}>
              NOTA (OPCIONAL EN APROBACIÓN · REQUERIDA EN RECHAZO)
            </label>
            <textarea
              value={rescheduleNote}
              onChange={(e) => setRescheduleNote(e.target.value)}
              rows={2}
              placeholder="Disponibilidad confirmada / Motivo de rechazo"
              style={{ width: '100%', marginTop: 6, background: 'var(--surface-2)', border: '1px solid var(--border-2)', color: 'var(--text)', padding: '10px 12px', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Btn onClick={() => approveReschedule.mutate()} disabled={approveReschedule.isPending}>
              {approveReschedule.isPending ? '…' : 'Aprobar y confirmar nuevo turno'}
            </Btn>
            <Btn variant="danger" onClick={() => rejectReschedule.mutate()} disabled={rejectReschedule.isPending || rescheduleNote.trim().length < 5}>
              {rejectReschedule.isPending ? '…' : 'Rechazar'}
            </Btn>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
            Tip: aprueba si hay combustible en {order.rescheduleRequest.newStationName} para esa fecha según los registros. Aprobar consume 1 intento del cliente (queda {2 - ((order.noShowAttempts ?? 0) + 1)} más). Rechazar mantiene los intentos intactos.
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>DETALLE</div>
          <table style={{ width: '100%', marginTop: 12, fontSize: 13 }}>
            <tbody>
              <Row k="Estado" v={order.status.toUpperCase()} />
              <Row k="Fecha de creación" v={new Date(order.createdAt).toLocaleString('es-MX')} />
              <Row k="Combustible" v={`${order.fuelVariantName} · ${order.fuelOctane} · $${order.fuelPrice.toFixed(2)}/L`} />
              <Row k="Estación" v={order.stationName} />
              <Row k="Dirección" v={order.stationAddr} />
              <Row k="Litros" v={`${order.liters} L`} />
              <Row k="Total" v={`$${order.total.toFixed(2)}`} />
              <Row k="Método" v={order.method} />
              <Row k="Slot" v={`${order.slotDate} ${order.slotTime} · Bomba ${order.slotPump}`} />
              {(order.noShowAttempts ?? 0) > 0 && (
                <Row k="Replanificaciones" v={`${order.noShowAttempts} de 2 usadas`} />
              )}
            </tbody>
          </table>
        </Card>

        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>ARCHIVO DE AUTORIZO</div>
          {order.authFile ? (
            <div style={{ marginTop: 12 }}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{order.authFile.key}</div>
              <div style={{ marginTop: 8, fontSize: 13 }}>{order.authFile.name}</div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-2)' }}>Estado: {order.authFile.status}</div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>Subido: {new Date(order.authFile.uploadedAt).toLocaleString('es-MX')}</div>
              {order.authFile.status === 'pending' && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <Btn onClick={async () => { await api('/api/admin/auth-files/review', { body: { orderId: order.id, decision: 'approved' } }); nav('/auth-files'); }}>Aprobar</Btn>
                  <Btn variant="danger" onClick={async () => { await api('/api/admin/auth-files/review', { body: { orderId: order.id, decision: 'rejected' } }); nav('/auth-files'); }}>Rechazar</Btn>
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 12, color: 'var(--text-3)', fontSize: 13 }}>Sin archivo (orden &lt; 60 L).</div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <tr>
      <td style={{ padding: '6px 0', color: 'var(--text-3)', fontSize: 12, width: 160 }}>{k}</td>
      <td style={{ padding: '6px 0' }}>{v}</td>
    </tr>
  );
}
